import { jwtDecode } from 'jwt-decode';
import { sessionStorageUtils } from './sessionStorage';
import type { DecodedToken, User } from '../types';

/**
 * Gets token from sessionStorage
 */
export const getToken = (): string | null => {
  return sessionStorageUtils.getSessionToken();
};

/**
 * Sets token in sessionStorage
 */
export const setToken = (token: string): void => {
  sessionStorageUtils.setSessionToken(token);
};

/**
 * Removes token from sessionStorage
 */
export const removeToken = (): void => {
  sessionStorageUtils.clearSessionToken();
};

/**
 * Decodes JWT token and returns user information
 */
export const getDecodedToken = (): DecodedToken | null => {
  const token = getToken();
  if (!token) return null;
  
  try {
    return jwtDecode<DecodedToken>(token);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Checks if token has expired
 */
export const isTokenExpired = (): boolean => {
  const decoded = getDecodedToken();
  if (!decoded) return true;
  
  // Convert timestamp to milliseconds and compare with current date
  return decoded.exp * 1000 < Date.now();
};

/**
 * Checks if user has a specific role
 */
export const hasRole = (requiredRole: string): boolean => {
  const decoded = getDecodedToken();
  if (!decoded) return false;
  
  return decoded.role === requiredRole;
};

/**
 * Gets user ID from token
 */
export const getUserId = (): number | null => {
  const decoded = getDecodedToken();
  return decoded?.userId || null;
};

/**
 * Gets user role from token
 */
export const getUserRole = (): string | null => {
  const decoded = getDecodedToken();
  return decoded?.role || null;
};

/**
 * Gets complete user information from token
 */
export const getUserFromToken = (): User | null => {
  const decoded = getDecodedToken();
  if (!decoded) return null;

  return {
    id: decoded.userId,
    email: decoded.email,
    firstName: decoded.firstName,
    lastName: decoded.lastName,
    role: decoded.role,
    fullName: `${decoded.firstName} ${decoded.lastName}`
  };
};

/**
 * Checks if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = getToken();
  if (!token) return false;
  
  return !isTokenExpired();
}; 